import { Trophy, Info, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { usePrivanaI18n } from "@/i18n/PrivanaI18nProvider.jsx";
import {
  achievementCatalogue,
  achievementCategories,
} from "../../../shared/achievementCatalogue.mjs";
import {
  achievementText,
  achievementName,
} from "../../../shared/achievementText.mjs";
import AchievementBadge from "../components/AchievementBadge";
import { formatProfileCalendarDate } from "../lib/profileOverview";
import { useMembersNativeBack } from "../lib/useMembersConversationOverlay";
export default function MemberAchievements({
  isMobileApp = false,
  clubCode,
  scope,
  get,
  onDetailsOpenChange,
}) {
  const { locale } = usePrivanaI18n(),
    t = achievementText(locale);
  const [resource, setResource] = useState({ scope, status: "loading" }),
    [filter, setFilter] = useState("all"),
    [category, setCategory] = useState("all");
  const location = useLocation(),
    navigate = useNavigate();
  const savedDetail = location.state?.achievementDetail;
  const informationOpen = isMobileApp && location.state?.achievementInformation === scope;
  const informationTrigger = useRef(null);
  const setInformationOpen = (open) => {
    if (open) navigate(`${location.pathname}${location.search}${location.hash}`, {
      state: { ...location.state, achievementInformation: scope },
      preventScrollReset: true,
    });
    else if (informationOpen) navigate(-1);
  };
  const currentAwards =
    resource.scope === scope ? resource.data?.awards || [] : [];
  const detail =
    savedDetail?.scope === scope
      ? {
          ...savedDetail,
          group: currentAwards.filter((a) => a.family === savedDetail.family),
        }
      : null;
  const setDetail = (next) => {
    if (!next) {
      if (savedDetail?.scope === scope) navigate(-1);
      return;
    }
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      state: {
        ...location.state,
        achievementDetail: {
          family: next.family || next.group[0].family,
          selected: next.selected,
          scope,
        },
      },
      replace: !!detail,
      preventScrollReset: true,
    });
  };
  const generation = useRef(0),
    active = useRef(scope),
    getRef = useRef(get),
    trigger = useRef(null),
    root = useRef(null);
  active.current = scope;
  getRef.current = get;
  useMembersNativeBack(!!detail || informationOpen, () => informationOpen ? setInformationOpen(false) : setDetail(null));
  useEffect(() => {
    onDetailsOpenChange?.(!!detail || informationOpen);
    return () => onDetailsOpenChange?.(false);
  }, [!!detail, informationOpen, onDetailsOpenChange]);
  const load = async () => {
    const gen = ++generation.current;
    setResource({ scope, status: "loading" });
    try {
      const r = await getRef.current(
          `/api/members-portal/${encodeURIComponent(clubCode)}/me/achievements`,
        ),
        body = typeof r?.json === "function" ? await r.json() : r;
      if (r?.ok === false || !body?.ok || !Array.isArray(body.data?.awards))
        throw Error("unavailable");
      if (gen === generation.current && active.current === scope)
        setResource({ scope, status: "ready", data: body.data });
    } catch {
      if (gen === generation.current && active.current === scope)
        setResource({ scope, status: "error" });
    }
  };
  useEffect(() => {
    setFilter("all");
    setCategory("all");
    void load();
    return () => {
      generation.current++;
    };
  }, [scope, clubCode]);
  const state = resource.scope === scope ? resource : { status: "loading" },
    awards = state.data?.awards || [];
  const groups = Object.values(
    awards.reduce((out, a) => {
      (out[a.family] ||= []).push(a);
      return out;
    }, {}),
  ).filter(
    (group) =>
      (category === "all" || group[0].category === category) &&
      group.some(
        (a) =>
          filter === "all" ||
          (filter === "earned" && a.state === "earned") ||
          (filter === "in_progress" &&
            !["earned", "not_applicable"].includes(a.state)),
      ),
  );
  const definition = (a) => achievementCatalogue.find((d) => d.code === a.code),
    name = (a) => achievementName(definition(a), locale);
  const badge = (a) => (
    <AchievementBadge
      category={a.category}
      threshold={a.state === "earned" ? a.threshold : 0}
      family={a.family}
      earned={a.state === "earned"}
      exceptional={["golf_ace", "golf_under_par", "racquet_perfect"].includes(
        a.family,
      )}
    />
  );
  const number = (n) => new Intl.NumberFormat(locale).format(n);
  const unit = (a, n) => t.units[a.unit]?.[n === 1 ? 0 : 1] || "";
  const amount = (a, n) => `${number(n)} ${unit(a, n)}`;
  const progress = (a) =>
    a.progress == null
      ? t.progressUnknown
      : `${number(a.progress)} / ${amount(a, a.threshold)}`;
  const milestones = (group, next) => (
    <span className="mp-achievement-milestones" aria-label={t.milestones}>
      {group.map((a) => (
        <span
          key={a.code}
          className={
            a.state === "earned"
              ? "is-earned"
              : a.code === next?.code
                ? "is-next"
                : "is-future"
          }
        >
          {amount(a, a.threshold)}{" "}
          {a.state === "earned" ? "✓" : a.code === next?.code ? "→" : "·"}
          <span className="sr-only">
            {a.state === "earned"
              ? t.earned
              : a.code === next?.code
                ? t.next
                : t.future}
          </span>
        </span>
      ))}
    </span>
  );
  return (
    <section className="mp-achievements" ref={root} aria-label={t.title}>
      {isMobileApp ? (
        <div className="mp-achievements-summary">
          <Trophy size={18} aria-hidden="true" />
          {state.status === "ready" ? <span>{t.compactEarnedCount(state.data.earned_count)}</span> : null}
          <button type="button" ref={informationTrigger} aria-label={t.informationLabel}
            aria-haspopup="dialog" aria-expanded={informationOpen}
            onClick={() => setInformationOpen(true)}><Info size={18} aria-hidden="true" /></button>
        </div>
      ) : <header className="mp-achievements-intro">
        <div>
          <p>{t.intro}</p>
        </div>
        {state.status === "ready" ? (
          <strong>{t.earnedCount(state.data.earned_count)}</strong>
        ) : null}
      </header>}
      <div className="mp-achievements-controls">
        <div role="group" aria-label={t.title}>
          {["all", "earned", "in_progress"].map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {t[f]}
            </button>
          ))}
        </div>
        <label>
          <span className="sr-only">{t.category}</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t.category}
          >
            <option value="all">
              {isMobileApp ? t.allCategories : `${t.all} · ${t.category}`}
            </option>
            {achievementCategories
              .filter((c) => awards.some((a) => a.category === c))
              .map((c) => (
                <option key={c} value={c}>
                  {t[c]}
                </option>
              ))}
          </select>
          {isMobileApp ? <ChevronDown className="mp-achievements-category-chevron" size={18} aria-hidden="true" /> : null}
        </label>
      </div>
      {state.status === "loading" ? (
        <p role="status">{t.loading}</p>
      ) : state.status === "error" ? (
        <div role="alert">
          <p>{t.error}</p>
          <button className="mp-ghost" onClick={load}>
            {t.retry}
          </button>
        </div>
      ) : (
        <>
          {state.data.source_unavailable ? (
            <p role="status">{t.source_unavailable}</p>
          ) : null}
          {state.data.pending ? <p role="status">{t.pending}</p> : null}
          {!isMobileApp && awards.some((a) => a.coverage === "partial") ? (
            <p className="mp-achievements-note">{t.partial}</p>
          ) : null}
          {[false, true].map((exceptionalSection) => {
            const collection = groups.filter(
              (group) => !t.families[group[0].family] === exceptionalSection,
            );
            if (!collection.length) return null;
            return (
              <div key={String(exceptionalSection)}>
                {exceptionalSection ? (
                  <h4 className="mp-achievements-section-title">
                    {t.exceptional}
                  </h4>
                ) : null}
                <div className="mp-achievement-grid">
                  {collection.map((group) => {
                    const earned = group.filter((a) => a.state === "earned"),
                      highest = earned.at(-1),
                      next = group.find((a) => a.state !== "earned"),
                      shown = highest || next,
                      exceptional =
                        group.length === 1 && !t.families[group[0].family];
                    if (!shown) return null;
                    return (
                      <button
                        className="mp-achievement-card"
                        key={shown.family}
                        onClick={(e) => {
                          trigger.current = e.currentTarget;
                          setDetail({ group, selected: shown.code, scope });
                        }}
                      >
                        <h4>
                          {exceptional ? name(shown) : t.families[shown.family]}
                        </h4>
                        {badge(shown)}
                        <span className="mp-achievement-status">
                          {exceptional
                            ? highest
                              ? t.earned
                              : t.notEarned
                            : highest
                              ? `${amount(highest, highest.threshold)} · ${t.earned}`
                              : t.starting}
                        </span>
                        {!exceptional ? (
                          <>
                            <span className="mp-achievement-next">
                              {next
                                ? next.progress == null
                                  ? `${t.next}: ${amount(next, next.threshold)}`
                                  : progress(next)
                                : amount(
                                    shown,
                                    shown.progress ?? shown.threshold,
                                  )}
                            </span>
                            {next && next.progress != null ? (
                              <progress
                                max={next.threshold}
                                value={Math.min(next.progress, next.threshold)}
                                aria-label={`${t.next}: ${amount(next, next.threshold)}`}
                              />
                            ) : null}
                            {milestones(group, next)}
                          </>
                        ) : (
                          <span className="mp-achievement-next">
                            {t.qualification(definition(shown))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!groups.length ? <p role="status">{t.empty}</p> : null}
        </>
      )}
      {isMobileApp ? <Dialog open={informationOpen} onOpenChange={setInformationOpen}>
        <DialogContent className="mp-achievement-dialog mp-achievements-information" overlayClassName="mp-achievements-information-overlay" closeLabel={t.close}
          onCloseAutoFocus={(e) => { e.preventDefault(); informationTrigger.current?.focus({ preventScroll: true }); }}>
          <DialogTitle className="sr-only">{t.informationLabel}</DialogTitle>
          <DialogDescription>{t.partial}</DialogDescription>
        </DialogContent>
      </Dialog> : null}
      <Dialog
        open={!!detail && detail.scope === scope}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent
          className="mp-achievement-dialog"
          overlayClassName="mp-achievement-overlay"
          closeLabel={t.close}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            trigger.current?.focus({ preventScroll: true });
          }}
        >
          <div className="mp-achievement-showcase-bar">{t.yourAchievement}</div>
          <div className="mp-achievement-dialog__scroll">
          {detail?.group?.length
            ? (() => {
                const a = detail.group.find((a) => a.code === detail.selected) || detail.group[0];
                const family = detail.group;
                const next = family.find((award) => !["earned", "not_applicable"].includes(award.state));
                const hasMilestones = family.length > 1;
                const status = a.state === "earned" ? t.earned
                  : ["source_unavailable", "not_applicable", "history_incomplete"].includes(a.state) ? t[a.state] : t.notEarned;
                return (
                  <>
                    <div className="mp-achievement-showcase">
                      <div className="mp-achievement-detail-badge">{badge(a)}</div>
                      <p className="mp-achievement-detail-status" data-earned={a.state === "earned"}>
                        {status}{a.state === "earned" && a.earned_date ? ` · ${formatProfileCalendarDate(a.earned_date, locale)}` : ""}
                      </p>
                      <DialogTitle>{name(a)}</DialogTitle>
                      <p className="mp-achievement-detail-category">{t[a.category]}</p>
                      <DialogDescription>{t.qualification({ ...definition(a), threshold: a.threshold })}</DialogDescription>
                    </div>
                    {hasMilestones ? <div className="mp-achievement-tier-selector">
                      <h4>{t.milestones}</h4>
                      <ul aria-label={t.tiers}>
                        {family.map(tier => <li key={tier.code}>
                          <button type="button" className={tier.state === "earned" ? "is-earned" : undefined} aria-pressed={tier.code === a.code}
                            aria-label={`${amount(tier, tier.threshold)} · ${name(tier)}`}
                            onClick={() => setDetail({ ...detail, selected: tier.code })}>
                            {amount(tier, tier.threshold)}
                            <span>{tier.state === "earned" ? `${t.earned} ✓` : t.notEarned}</span>
                          </button>
                        </li>)}
                      </ul>
                    </div> : null}
                    {hasMilestones ? <section className="mp-achievement-progress" aria-label={t.next}>
                      <h4>{next ? `${t.next}: ${name(next)}` : family.every(award => award.state === "earned") ? t.complete : t.not_applicable}</h4>
                      {next ? <>
                        <p>{next.state === "source_unavailable" ? t.source_unavailable : next.progress == null ? t.progressUnknown : t.progressOf(number(next.progress), amount(next, next.threshold))}</p>
                        {next.progress != null && next.state !== "source_unavailable" ? <progress max={next.threshold} value={Math.min(next.progress, next.threshold)} aria-label={`${t.next}: ${amount(next, next.threshold)}`} /> : null}
                      </> : null}
                    </section> : null}
                  </>
                );
              })()
            : null}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
